/**
 * Confirmation between a press and the Codex capacity-alert brake moving
 * (#2575). Opening leads with the unrecallable-mail consequence; closing
 * buys the reason field so the resulting "off" is self-describing.
 */
import type { ReactNode } from 'react';
import { useT } from '../../i18n/index.ts';
import { ConfirmDialog } from '../../ui/index.ts';

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly enabling: boolean;
  readonly loading: boolean;
  readonly reasonField: ReactNode;
  readonly onConfirm: () => void;
}

export function CodexCapacitySwitchDialog({
  open,
  onOpenChange,
  enabling,
  loading,
  reasonField,
  onConfirm,
}: Props): JSX.Element {
  const t = useT();
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      tone={enabling ? 'danger' : 'default'}
      title={t(enabling ? 'codex_capacity.switch.enable_title' : 'codex_capacity.switch.disable_title')}
      body={
        <div className="flex flex-col gap-3">
          <p
            className={
              enabling
                ? 'm-0 rounded border border-bad-line bg-bad-dim/50 px-2.5 py-2 text-dense text-bad-bright'
                : 'm-0 rounded border border-warn-line bg-warn-dim/50 px-2.5 py-2 text-dense text-warn'
            }
            data-testid="codex-capacity-switch-confirm-consequence"
          >
            {t(
              enabling
                ? 'codex_capacity.switch.enable_consequence'
                : 'codex_capacity.switch.disable_consequence',
            )}
          </p>
          <p className="m-0">
            {t(
              enabling
                ? 'codex_capacity.switch.enable_detail'
                : 'codex_capacity.switch.disable_detail',
            )}
          </p>
          {reasonField}
        </div>
      }
      confirmLabel={t(
        enabling ? 'codex_capacity.switch.enable_action' : 'codex_capacity.switch.disable_action',
      )}
      cancelLabel={t('common.cancel')}
      closeLabel={t('common.close')}
      loading={loading}
      onConfirm={onConfirm}
      testId="codex-capacity-switch-confirm"
    />
  );
}
